import { Dictionary } from 'lodash';
import { Moment } from 'moment';
import { useCallback } from 'react';
import { Team, User } from 'api/models';
import { AllocationEvent } from 'components/organisms/resource-management/event-types';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import { ProjectsWithMembers } from 'components/organisms/resource-management/types';
import { asAllocationPayload } from 'components/organisms/resource-management/utils/events-conversion/common';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { useGetAllTasks } from 'data/templates/projects';
import { TemplateAssignee } from '../types';
import { asTemplateAllocation } from '../utils/as-template-allocation';
import { autoAllocateFromTasks } from '../utils/auto-allocate';
import { mergeAllocationsForAssignee } from '../utils/merge-allocations';
import { useAllocationAsProjectEvents } from './project-events';

interface Props {
  project: ProjectsWithMembers;
  workingDaysConfig: WorkingDaysConfiguration;
  relativeDate: Moment;
  userLookup: Dictionary<User>;
  teamLookup: Dictionary<Team>;
  allTasksAssignees: TemplateAssignee;
  isHoursPerWeek?: boolean;
}

export const useAutoFillAllocationsFromTasks = (
  props: Props,
): {
  autoFillAllocationsFromTasks: () => AllocationEvent[];
} => {
  const allTasks = useGetAllTasks();
  const { allTasksAssignees, workingDaysConfig, relativeDate, isHoursPerWeek } =
    props;
  const { convertAllocationsAsProjectEvents } =
    useAllocationAsProjectEvents(props);
  const autoFillAllocationsFromTasks = useCallback(() => {
    const allocationsFromTasks = autoAllocateFromTasks(
      Object.values(allTasks),
      isHoursPerWeek,
    );
    const events = convertAllocationsAsProjectEvents(allocationsFromTasks);
    const asAllocation = asTemplateAllocation(relativeDate, isHoursPerWeek);

    return convertAllocationsAsProjectEvents(
      mergeAllocationsForAssignee(
        allTasksAssignees,
        workingDaysConfig,
      )(events).map((x) =>
        asAllocation(asAllocationPayload(new AllocationEventModel(x))),
      ),
    );
  }, [
    allTasks,
    allTasksAssignees,
    convertAllocationsAsProjectEvents,
    relativeDate,
    workingDaysConfig,
    isHoursPerWeek,
  ]);

  return {
    autoFillAllocationsFromTasks,
  };
};
